import { useEffect, useState } from "react";
import jsPDF from "jspdf";
import { supabase } from "../supabaseClient";
import YomaLogo from "../components/YomaLogo";
import { YOMA_LOGO_SVG } from "../utils/yomaLogoSvg";

const GOLD       = "#C9A96E";
const GOLD_PALE  = "#F0DEB4";
const GOLD_BG    = "#FEF9EE";
const INK        = "#1C1915";
const INK_SOFT   = "#4A4540";
const INK_MUTED  = "#9A9390";
const BORDER     = "#E7E1D8";
const SOFT_BG    = "#F7F5F2";
const WHITE      = "#FFFFFF";
const GREEN      = "#1A7A44";
const GREEN_BG   = "#E8F6EF";
const RED        = "#B91C1C";
const RED_BG     = "#FEF2F2";
const ORANGE     = "#C2410C";
const ORANGE_BG  = "#FFF7ED";

const STATUT_FILTERS = [
  { id: "tous", label: "Tous" },
  { id: "brouillon", label: "Brouillon" },
  { id: "envoye", label: "Envoyé" },
  { id: "en_discussion", label: "En discussion" },
  { id: "accepte", label: "Accepté" },
  { id: "refuse", label: "Refusé" },
  { id: "expire", label: "Expiré" },
];

const STATUT_OPTIONS = [
  { id: "brouillon", label: "Brouillon" },
  { id: "envoye", label: "Envoyé" },
  { id: "en_discussion", label: "En discussion" },
  { id: "accepte", label: "Accepté" },
  { id: "refuse", label: "Refusé" },
  { id: "expire", label: "Expiré" },
];

function getStatutLabel(statut) {
  if (!statut) return "—";
  const map = {
    brouillon: "Brouillon",
    envoye: "Envoyé",
    signe: "Accepté",
    en_discussion: "En discussion",
    accepte: "Accepté",
    refuse: "Refusé",
    expire: "Expiré",
  };
  return map[statut] || statut;
}

function getStatutBadgeStyle(statut) {
  switch (statut) {
    case "brouillon":   return { bg: SOFT_BG, color: INK_MUTED, border: BORDER };
    case "envoye":      return { bg: GOLD_BG, color: GOLD, border: GOLD_PALE };
    case "signe":
    case "accepte":     return { bg: GREEN_BG, color: GREEN, border: GREEN };
    case "en_discussion": return { bg: ORANGE_BG, color: ORANGE, border: ORANGE };
    case "refuse":      return { bg: RED_BG, color: RED, border: RED };
    case "expire":      return { bg: SOFT_BG, color: INK_MUTED, border: BORDER };
    default:            return { bg: SOFT_BG, color: INK_SOFT, border: BORDER };
  }
}

function fmtMontant(n) {
  if (n == null || n === "") return "—";
  return Number(n).toLocaleString("fr-FR") + " DH";
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function PropositionsPage({ setActiveTab, cabinetId = null }) {
  const [propositions, setPropositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatut, setFilterStatut] = useState("tous");
  const [updatingId, setUpdatingId] = useState(null);

  function genererPdfDepuisLigne(p) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const marginX = 56;
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 160;
    const logoHeight = 55;
    const logoX = (pageWidth - logoWidth) / 2;
    let y = 64;

    const dateStr = new Date(p.created_at || Date.now()).toLocaleDateString("fr-FR");
    const clientName = p.client_name || "";
    const projectName = p.project_name || "";

    try {
      doc.addImage(YOMA_LOGO_SVG, "SVG", logoX, y - 10, logoWidth, logoHeight);
    } catch (e) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("YOMA DESIGN", marginX, y);
    }
    doc.setDrawColor(201, 169, 110);
    doc.setLineWidth(1);
    doc.line(marginX, y + logoHeight - 3, marginX + 120, y + logoHeight - 3);
    y += logoHeight + 25;

    doc.setFontSize(20);
    doc.text("Proposition contractuelle", marginX, y);
    y += 22;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Date : ${dateStr}`, marginX, y);
    y += 28;

    const addSectionTitle = (title) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(title, marginX, y);
      y += 8;
      doc.setDrawColor(231, 225, 216);
      doc.setLineWidth(0.7);
      doc.line(marginX, y, 539, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
    };

    const addField = (label, value) => {
      if (y > 770) {
        doc.addPage();
        y = 64;
      }
      doc.setFont("helvetica", "bold");
      doc.text(`${label} :`, marginX, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(value || "—"), marginX + 130, y);
      y += 18;
    };

    addSectionTitle("1. Informations client");
    addField("Client", clientName);
    addField("Projet", projectName);
    y += 6;

    addSectionTitle("2. Informations mission");
    addField("Type de mission", p.mission_type || "—");
    addField("Pays", p.pays || "—");
    addField("Surface (m²)", p.surface_m2 != null ? String(p.surface_m2) : "—");
    addField("Complexité", p.complexite || "—");
    y += 6;

    addSectionTitle("3. Détail financier");
    addField("Montant", p.montant != null ? fmtMontant(p.montant) : "—");
    addField("Acompte (%)", p.acompte_pct != null ? String(p.acompte_pct) : "—");
    addField("Échéancier - acompte", p.echeancier_acompte || "—");
    addField("Échéancier - lancement", p.echeancier_lancement || "—");
    addField("Échéancier - fin de projet", p.echeancier_fin_projet || "—");
    addField("Statut", getStatutLabel(p.statut));

    const footerY = 812;
    doc.setDrawColor(231, 225, 216);
    doc.setLineWidth(0.5);
    doc.line(marginX, footerY - 24, 539, footerY - 24);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("YOMA DESIGN — Proposition contractuelle", marginX, footerY - 10);

    const safeClient = clientName || "client";
    const safeProject = projectName || "projet";
    const filename = `Proposition_${safeClient}_${safeProject}.pdf`.replace(/[\\/:*?"<>|]+/g, "_");
    doc.save(filename);
  }

  async function setStatut(propositionId, newStatut, projectId) {
    setUpdatingId(propositionId);
    const { error } = await supabase
      .from("contract_proposals")
      .update({ statut: newStatut, updated_at: new Date().toISOString() })
      .eq("id", propositionId);
    if (error) {
      console.error("Erreur mise à jour statut:", error);
    } else {
      setPropositions((prev) =>
        prev.map((p) => (p.id === propositionId ? { ...p, statut: newStatut } : p))
      );
      // Devis accepté → lancer le projet : mettre à jour le projet lié (project_id) en statut "conception"
      if (newStatut === "accepte" && projectId) {
        await supabase
          .from("projects")
          .update({ status: "conception" })
          .eq("id", projectId)
          .eq("cabinet_id", cabinetId);
      }
    }
    setUpdatingId(null);
  }

  useEffect(() => {
    if (cabinetId == null) return;
    let cancelled = false;
    setLoading(true);
    supabase
      .from("contract_proposals")
      .select("*")
      .eq("cabinet_id", cabinetId)
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("Erreur chargement propositions:", error);
          setPropositions([]);
        } else {
          setPropositions(data || []);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cabinetId]);

  const filtered = propositions.filter((p) => {
    const matchSearch = !search || [p.client_name, p.project_name].some(
      (v) => (v || "").toLowerCase().includes(search.toLowerCase())
    );
    if (!matchSearch) return false;
    if (filterStatut === "tous") return true;
    if (filterStatut === "accepte") return p.statut === "signe" || p.statut === "accepte";
    return p.statut === filterStatut;
  });

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: INK_MUTED }}>Chargement...</div>;
  }

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 1100, margin: "0 auto" }}><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 16 }}><div><YomaLogo width={130} height={40} /><div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 4 }}>
            CRM Devis
          </div><div style={{ fontSize: 20, fontWeight: 800, color: INK }}>Propositions / Devis</div><div style={{ fontSize: 12, color: INK_MUTED, marginTop: 4 }}>
            {filtered.length} proposition{filtered.length !== 1 ? "s" : ""}
          </div></div><button
          onClick={() => setActiveTab && setActiveTab("projets")}
          style={{
            padding: "10px 20px",
            background: GOLD,
            border: "none",
            borderRadius: 9,
            fontSize: 12,
            fontWeight: 700,
            color: WHITE,
            cursor: "pointer",
          }}
        >
          Nouvelle proposition
        </button></div><div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}><input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par client ou projet…"
          style={{
            flex: "1 1 220px",
            minWidth: 220,
            padding: "10px 14px",
            borderRadius: 8,
            border: `1px solid ${BORDER}`,
            fontSize: 13,
            color: INK,
            background: WHITE,
            outline: "none",
            boxSizing: "border-box",
          }}
        /><div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {STATUT_FILTERS.map((s) => {
            const active = filterStatut === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setFilterStatut(s.id)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: `1px solid ${active ? GOLD : BORDER}`,
                  background: active ? GOLD_BG : WHITE,
                  color: active ? GOLD : INK_SOFT,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: INK_MUTED }}>Chargement…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: INK_MUTED }}>
            {propositions.length === 0
              ? "Aucune proposition enregistrée. Créez une proposition depuis un projet."
              : "Aucun résultat pour ce filtre ou cette recherche."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}><thead><tr style={{ borderBottom: `2px solid ${BORDER}`, background: SOFT_BG }}><th style={{ textAlign: "left", padding: "12px 16px", fontWeight: 700, color: INK_MUTED }}>Client</th><th style={{ textAlign: "left", padding: "12px 16px", fontWeight: 700, color: INK_MUTED }}>Projet</th><th style={{ textAlign: "right", padding: "12px 16px", fontWeight: 700, color: INK_MUTED }}>Montant</th><th style={{ textAlign: "left", padding: "12px 16px", fontWeight: 700, color: INK_MUTED }}>Statut</th><th style={{ textAlign: "left", padding: "12px 16px", fontWeight: 700, color: INK_MUTED }}>Créé le</th><th style={{ textAlign: "right", padding: "12px 16px", fontWeight: 700, color: INK_MUTED }}></th></tr></thead><tbody>
                {filtered.map((p) => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${BORDER}` }}><td style={{ padding: "12px 16px", fontWeight: 600 }}>{p.client_name || "—"}</td><td style={{ padding: "12px 16px" }}>{p.project_name || "—"}</td><td style={{ padding: "12px 16px", textAlign: "right" }}>{fmtMontant(p.montant)}</td><td style={{ padding: "12px 16px" }}><div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}><span
                          style={{
                            padding: "4px 10px",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            border: `1px solid ${getStatutBadgeStyle(p.statut).border}`,
                            background: getStatutBadgeStyle(p.statut).bg,
                            color: getStatutBadgeStyle(p.statut).color,
                          }}
                        >
                          {getStatutLabel(p.statut)}
                        </span><select
                          value={p.statut === "signe" ? "accepte" : (p.statut || "brouillon")}
                          onChange={(e) => setStatut(p.id, e.target.value, p.project_id)}
                          disabled={!!updatingId}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 8,
                            border: `1px solid ${BORDER}`,
                            fontSize: 12,
                            color: INK,
                            background: WHITE,
                            cursor: updatingId ? "wait" : "pointer",
                            minWidth: 130,
                          }}
                        >
                          {STATUT_OPTIONS.map((o) => (
                            <option key={o.id} value={o.id}>{o.label}</option>
                          ))}
                        </select></div></td><td style={{ padding: "12px 16px", color: INK_SOFT }}>{fmtDate(p.created_at)}</td><td style={{ padding: "12px 16px", textAlign: "right" }}><div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}><button
                        type="button"
                        onClick={() => genererPdfDepuisLigne(p)}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: `1px solid ${BORDER}`,
                          background: WHITE,
                          color: INK_SOFT,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Générer PDF
                      </button><button
                        type="button"
                        onClick={() => setActiveTab && setActiveTab("proposition-contractuelle:" + (p.project_id || ""))}
                        style={{
                          padding: "8px 14px",
                          borderRadius: 8,
                          border: `1px solid ${GOLD}`,
                          background: GOLD_BG,
                          color: GOLD,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Ouvrir
                      </button></div></td></tr>
                ))}
              </tbody></table></div>
        )}
      </div></div>
  );
}
